/**
 * App-wide crash safety net.
 *
 *  - AppErrorBoundary: a class boundary wrapping the whole tree (catches throws
 *    in AuthProvider / router setup / anything outside a route).
 *  - RouteErrorComponent: passed to TanStack Router `errorComponent` so a throw
 *    inside one route subtree degrades to a card instead of white-screening the
 *    entire CRM.
 *
 * Goal: no single component error ever takes down the whole app.
 */
import { Component, type ReactNode, type ErrorInfo } from 'react';

function Fallback({ scope, message, onReset }: { scope: 'app' | 'route'; message?: string; onReset?: () => void }) {
  const isApp = scope === 'app';
  return (
    <div className={isApp ? 'min-h-screen grid place-items-center bg-[var(--color-page,#f4f8f8)] p-6' : 'p-6'}>
      <div className="max-w-md w-full rounded-xl border border-[var(--color-line)] bg-white p-6 text-center shadow-sm">
        <div className="text-lg font-bold text-[var(--color-navy,#032e5b)]">Something went wrong</div>
        <p className="mt-2 text-sm text-[var(--color-ink-muted)]">
          {isApp
            ? 'The app hit an unexpected error. Reloading usually fixes it. If it keeps happening, let an admin know.'
            : 'This section hit an error. The rest of the app is fine — try again or head back.'}
        </p>
        {message && (
          <pre className="mt-3 max-h-32 overflow-auto rounded bg-[var(--color-surface-1)] p-2 text-left text-[11px] text-[var(--color-ink-soft)] whitespace-pre-wrap">{message}</pre>
        )}
        <div className="mt-4 flex justify-center gap-2">
          {onReset && (
            <button onClick={onReset} className="rounded-lg bg-[var(--color-navy,#032e5b)] text-white text-sm font-semibold px-4 py-2 hover:opacity-90">
              Try again
            </button>
          )}
          <button onClick={() => window.location.reload()} className="rounded-lg border border-[var(--color-line)] text-sm font-semibold px-4 py-2 hover:bg-[var(--color-surface-1)]">
            Reload
          </button>
        </div>
      </div>
    </div>
  );
}

/** TanStack Router errorComponent — receives the thrown error + a reset fn. */
export function RouteErrorComponent({ error, reset }: { error: Error; reset?: () => void }) {
  // Surface to telemetry/console so silent failures are visible.
  console.error('[route error]', error);
  return <Fallback scope="route" message={import.meta.env.DEV ? error?.message : undefined} onReset={reset} />;
}

export class AppErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  override state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  override componentDidCatch(error: Error, info: ErrorInfo) { console.error('[app error]', error, info); }
  override render() {
    if (this.state.error) {
      return <Fallback scope="app" message={import.meta.env.DEV ? this.state.error.message : undefined} onReset={() => this.setState({ error: null })} />;
    }
    return this.props.children;
  }
}
